import { useSearchParams } from "react-router-dom";

import { SearchParams } from "@/shared/environment";
import { actions } from "@/shared/state/visualization";
import {
  useDatasources,
  useVizDispatch,
} from "@/shared/state/visualization/hooks";
import { type FileSource } from "@/shared/state/visualization/schema/v1";

import { useWorkspaceLoaderData } from "../loader";

/**
 * Returns `addFiles`, which merges file IDs into the open visualization
 * workspace's datasources (existing dataset sources and files are kept,
 * duplicates dropped) and rewrites the URL's dataset and file search params to
 * match. A workspace opened from a shared workspace link gets the merge but not
 * the rewrite.
 *
 * Files the loader expanded out of `?session=` stay in the datasources but out
 * of the rewritten `?file=`, so a session of any size costs one search param.
 * The rewrite leaves `?session=` alone, so a reload lists the session again and
 * derives the same files. Adding one of those files by hand changes nothing:
 * the existing datasource stands, session marker included, so the file stays
 * out of `?file=`.
 */
export function useAddFilesToWorkspace() {
  const dispatch = useVizDispatch();
  const [, setSearchParams] = useSearchParams();
  const workspaceLoaderData = useWorkspaceLoaderData();
  const datasources = useDatasources();

  const isSharedWorkspace =
    workspaceLoaderData.configSource?.type === "workspace";

  const addFiles = (fileIdsToAdd: string[]) => {
    // Keep every existing dataset source — a workspace can hold several
    // (e.g. when opened from an event spanning multiple datasets).
    const datasetSources = datasources.filter((ds) => ds.type === "dataset");

    // Each existing file datasource carries what the loader recorded about the
    // file: the parent dataset it resolved (`associationId`) and the session it
    // was expanded from. Passing them through whole keeps both.
    const existingFileSources: FileSource[] = datasources.filter(
      (ds) => ds.type === "file",
    );

    const existingFileIds = new Set(
      existingFileSources.map((ds) => ds.value.fileId),
    );
    const addedFileSources: FileSource[] = [...new Set(fileIdsToAdd)]
      .filter((fileId) => !existingFileIds.has(fileId))
      .map((fileId) => ({ type: "file", value: { fileId } }));

    const fileSources = [...existingFileSources, ...addedFileSources];

    dispatch(actions.setDatasources([...datasetSources, ...fileSources]));

    // A workspace opened from a shared workspace link gets its contents from
    // the persisted workspace record, so dataset/file search params don't
    // apply to it.
    if (isSharedWorkspace) {
      return;
    }

    setSearchParams(
      (oldParams) => {
        const params = new URLSearchParams(oldParams);

        params.delete(SearchParams.DATASET);
        datasetSources.forEach((ds) => {
          params.append(SearchParams.DATASET, ds.value.datasetId);
        });

        params.delete(SearchParams.FILE);
        fileSources
          .filter((ds) => ds.derivedFrom === undefined)
          .forEach((ds) => {
            params.append(SearchParams.FILE, ds.value.fileId);
          });

        return params;
      },
      { preventScrollReset: true, replace: true },
    );
  };

  return { addFiles };
}
